import { useMemo } from 'react';
import { useVfxPreview } from '@/hooks/useVfxPreview';
import { computeObjectContainContentRect } from '@/lib/videoSubtitlePreview';
import type { EffectTimelineClip } from '@/vfx/types';

interface ContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface VfxPreviewOverlayProps {
  mediaRef: React.RefObject<HTMLVideoElement | HTMLImageElement | null>;
  stageRef?: React.RefObject<HTMLElement | null>;
  stageWidth?: number;
  stageHeight?: number;
  aspectRatio?: string;
  currentTime: number;
  currentTimeRef?: React.RefObject<number>;
  effects: EffectTimelineClip[];
  enabled?: boolean;
  contentRect?: ContentRect;
  className?: string;
}

/**
 * VFX composited preview aligned to the object-contain media frame so effects
 * cover the picture without stretching it to the stage aspect ratio.
 */
export function VfxPreviewOverlay({
  mediaRef,
  stageRef,
  stageWidth = 0,
  stageHeight = 0,
  aspectRatio = '16:9',
  currentTime,
  currentTimeRef,
  effects,
  enabled = true,
  contentRect,
  className = '',
}: VfxPreviewOverlayProps) {
  const frame = useMemo(() => {
    if (stageWidth > 0 && stageHeight > 0) {
      const rect = contentRect?.width
        ? contentRect
        : computeObjectContainContentRect(stageWidth, stageHeight, aspectRatio);
      return {
        left: rect.left || 0,
        top: rect.top || 0,
        width: Math.max(2, Math.round(rect.width || 0)),
        height: Math.max(2, Math.round(rect.height || 0)),
      };
    }
    const stage = stageRef?.current;
    if (stage) {
      const bounds = stage.getBoundingClientRect();
      const w = Math.round(bounds.width);
      const h = Math.round(bounds.height);
      if (w > 1 && h > 1) {
        const rect = computeObjectContainContentRect(w, h, aspectRatio);
        return {
          left: rect.left || 0,
          top: rect.top || 0,
          width: Math.max(2, Math.round(rect.width || 0)),
          height: Math.max(2, Math.round(rect.height || 0)),
        };
      }
    }
    return { left: 0, top: 0, width: 0, height: 0 };
  }, [stageWidth, stageHeight, contentRect, stageRef, aspectRatio]);

  const { canvasRef } = useVfxPreview(mediaRef, {
    enabled,
    currentTime,
    currentTimeRef,
    effects,
    fps: 30,
    width: frame.width,
    height: frame.height,
  });

  const hasEffects = (effects || []).length > 0;

  if (!enabled || !hasEffects || frame.width < 2 || frame.height < 2) return null;

  // Keep the canvas mounted and visible; RAF clears it when no effect is active.
  // Gating on React-sampled time hid VFX during Play Preview (time only commits ~4Hz).
  return (
    <div
      className={`pointer-events-none absolute inset-0 z-[20] ${className}`.trim()}
      data-testid="vfx-preview-overlay"
      aria-hidden
    >
      <canvas
        ref={canvasRef}
        width={frame.width}
        height={frame.height}
        className="absolute"
        style={{
          left: frame.left,
          top: frame.top,
          width: frame.width,
          height: frame.height,
        }}
      />
    </div>
  );
}

export default VfxPreviewOverlay;
